import {useState} from 'react';
import {FolderUp} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {Switch} from '@common-web/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {API_ENVIRONMENTS, type ApiEnvironment} from '@common/constants/environments';
import {
  ftpWritesEnabled,
  type FtpWritesConfig,
  type ToggleableFtpWriteEnvironment,
} from '@common/ftpWrites';

type FtpWritesConfigSectionProps = {
  value: FtpWritesConfig | undefined;
  /** The environment the API answering this page runs in. */
  currentEnvironment: ApiEnvironment | undefined;
  disabled?: boolean;
  onChange: (environment: ToggleableFtpWriteEnvironment, enabled: boolean) => void;
};

export function FtpWritesConfigSection({
  value,
  currentEnvironment,
  disabled,
  onChange,
}: FtpWritesConfigSectionProps) {
  // Confirmed first: switching a non-prod environment on, or prod off.
  const [confirming, setConfirming] = useState<{
    name: ToggleableFtpWriteEnvironment;
    enabled: boolean;
  } | null>(null);

  return (
    <div className='space-y-4'>
      <div>
        <div className='flex items-center gap-2'>
          <FolderUp className='h-4 w-4 text-brand-gray' />
          <h3 className='text-sm font-semibold text-brand-navy'>Uploads to CBS's FTP</h3>
        </div>
        <p className='mt-1 text-xs text-brand-gray'>
          There is only the one live FTP, so an upload from any environment lands in the group's
          real folder. This page can only change the environment its API runs in
          {currentEnvironment ? (
            <>
              {' '}
              (<span className='font-medium'>{currentEnvironment}</span>)
            </>
          ) : null}
          .
        </p>
      </div>

      <div className='divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white px-4'>
        {API_ENVIRONMENTS.map((name) => {
          const isCurrent = name === currentEnvironment;
          const enabled = ftpWritesEnabled(name, value);
          return (
            <div key={name} className='flex items-center justify-between gap-4 py-3'>
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <div className='text-sm font-medium text-brand-navy'>{name}</div>
                  {isCurrent && (
                    <Badge variant='outline' className='text-xs'>
                      this environment
                    </Badge>
                  )}
                </div>
                <div className='text-xs text-brand-gray'>
                  {enabled ? 'On' : 'Off'}
                  {name === 'prod' ? ' (on unless switched off)' : ''}
                  {isCurrent ? '.' : `. Change it from the ${name} internal portal.`}
                </div>
              </div>
              <Switch
                checked={enabled}
                disabled={disabled || !isCurrent}
                onCheckedChange={(checked) =>
                  checked === (name === 'prod')
                    ? onChange(name, checked)
                    : setConfirming({name, enabled: checked})
                }
                aria-label={`Uploads to CBS's FTP from ${name}`}
              />
            </div>
          );
        })}
      </div>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming?.enabled
                ? `Upload to CBS's FTP from ${confirming.name}?`
                : `Turn off uploads in ${confirming?.name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming?.enabled
                ? `Files uploaded from the ${confirming.name} portal will be saved in groups' real folders on CBS's FTP until this is switched off. Nothing is ever overwritten or deleted.`
                : "Nobody will be able to upload documents to CBS's FTP from the portal until this is switched back on."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming) onChange(confirming.name, confirming.enabled);
                setConfirming(null);
              }}
            >
              {confirming?.enabled ? 'Turn on' : 'Turn off'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
